import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { getApiGalaxyServer } from 'services/api';
import { grafanaQueryKeys } from '../query/grafanaQueryKeys';

interface UseGrafanaDashboardOptions {
  dashboardUid: string;
  panelId?: number;
  /** Grafana template variables to pass as var-* query params (e.g. { env: "Party", tenant: "party" }) */
  variables?: Record<string, string | number>;
  width?: number;
  height?: number;
  from?: string;
  to?: string;
  /**
   * Retry policy, forwarded to react-query. Defaults are deliberately left alone so
   * existing callers keep the behaviour they were written against; a caller whose panel
   * is expensive to render should lower these, because the image renderer is a single
   * replica and the default 3 retries turn one slow render into four concurrent ones.
   */
  retry?: number;
  retryDelay?: number;
}

/**
 * Fetches a Grafana panel as a server-rendered PNG image.
 * Returns a Blob — the caller is responsible for creating/revoking
 * the object URL to keep the URL lifecycle tied to the render lifecycle.
 */
export const useGrafanaDashboard = ({
  dashboardUid,
  panelId,
  variables,
  width,
  height,
  from,
  to,
  retry,
  retryDelay,
}: UseGrafanaDashboardOptions) => {
  return useAppQuery<Blob>({
    queryKey: grafanaQueryKeys.detail({
      identifier: 'useGrafanaDashboard',
      dashboardUid,
      panelId,
      variables,
      width,
      height,
      from,
      to,
    }),
    queryFn: async ({ token }) => {
      const params = new URLSearchParams();
      if (panelId !== undefined && panelId !== null) params.set('panelId', String(panelId));
      if (width) params.set('width', String(width));
      if (height) params.set('height', String(height));
      if (from) params.set('from', from);
      if (to) params.set('to', to);

      // Pass template variables as var-* query params
      if (variables) {
        for (const [key, value] of Object.entries(variables)) {
          if (value !== undefined && value !== null) {
            params.set(`var-${key}`, String(value));
          }
        }
      }

      const qs = params.toString();
      const query = qs ? `?${qs}` : '';
      const path = `/grafana/dashboards/${dashboardUid}/render${query}`;

      const response = await getApiGalaxyServer(
        path,
        { withCredentials: true, responseType: 'blob' },
        token,
      );
      return response?.data as Blob;
    },
    enabled: !!variables,
    ...(retry !== undefined ? { retry } : {}),
    ...(retryDelay !== undefined ? { retryDelay } : {}),
  });
};
